/**
 * Fetch client for team_crm admin UI.
 *
 * Auth: Cloudflare Access injects Cf-Access-Jwt-Assertion on proxied requests.
 * We use credentials: 'include' so the Access session cookie is forwarded.
 * No manual JWT handling required.
 */

// Default: empty = same-origin. Admin UI calls team.quantumsecuritydefence.com/api/*
// which routes to the Worker via the route binding in wrangler.toml.
// Override via VITE_API_BASE for local dev.
const API_BASE = import.meta.env.VITE_API_BASE ?? ''

export class ApiError extends Error {
  constructor(
    public status: number,
    public code: string,
    message: string,
    public details?: unknown,
  ) {
    super(message)
    this.name = 'ApiError'
  }
}

async function request<T>(
  method: string,
  path: string,
  body?: unknown,
  options: RequestInit = {},
): Promise<T> {
  const url = `${API_BASE}${path}`

  const headers: Record<string, string> = {
    'Content-Type': 'application/json',
    Accept: 'application/json',
  }

  const res = await fetch(url, {
    method,
    credentials: 'include',
    headers,
    body: body !== undefined ? JSON.stringify(body) : undefined,
    ...options,
  })

  if (!res.ok) {
    let errorBody: { error?: string; code?: string; details?: unknown } = {}
    try {
      errorBody = await res.json()
    } catch {
      // ignore parse error
    }
    throw new ApiError(
      res.status,
      errorBody.code ?? 'unknown',
      errorBody.error ?? `HTTP ${res.status}`,
      errorBody.details,
    )
  }

  // 204 No Content
  if (res.status === 204) {
    return undefined as T
  }

  return res.json() as Promise<T>
}

export const api = {
  get: <T>(path: string, options?: RequestInit) =>
    request<T>('GET', path, undefined, options),

  post: <T>(path: string, body?: unknown) =>
    request<T>('POST', path, body),

  patch: <T>(path: string, body?: unknown) =>
    request<T>('PATCH', path, body),

  delete: <T>(path: string) =>
    request<T>('DELETE', path),

  /** Upload a file (multipart). Does NOT set Content-Type — browser does. */
  upload: <T>(path: string, formData: FormData): Promise<T> => {
    const url = `${API_BASE}${path}`
    return fetch(url, {
      method: 'POST',
      credentials: 'include',
      body: formData,
    }).then(async (res) => {
      if (!res.ok) {
        let errorBody: { error?: string; code?: string; details?: unknown } = {}
        try {
          errorBody = await res.json()
        } catch {
          // ignore
        }
        throw new ApiError(
          res.status,
          errorBody.code ?? 'unknown',
          errorBody.error ?? `HTTP ${res.status}`,
          errorBody.details,
        )
      }
      return res.json() as Promise<T>
    })
  },
}

// ── Typed endpoint helpers ───────────────────────────────────────────────────
// These mirror API_CONTRACT.md exactly. Add query params as URLSearchParams.

import type {
  AdminMe,
  ExtensionToken,
  ExtensionTokenMinted,
  ICP,
  Lead,
  LeadDetail,
  ImportBatch,
  ImportUploadResponse,
  ScrapeJob,
  Sequence,
  OptOut,
  Settings,
  RateCounter,
  DashboardData,
  AnalyticsData,
  BulkActionRequest,
  EnrolRequest,
  PaginatedResponse,
} from '@/schemas'

// Auth
export const adminMe = () =>
  api.get<AdminMe>('/api/v1/admin/me')

// Extension tokens
export const listExtensionTokens = () =>
  api.get<{ tokens: ExtensionToken[] }>('/api/v1/admin/extension-tokens')

export const mintExtensionToken = (userAgent?: string) =>
  api.post<ExtensionTokenMinted>('/api/v1/admin/extension-tokens', { user_agent: userAgent })

export const revokeExtensionToken = (tokenId: string) =>
  api.delete<{ success: boolean }>(`/api/v1/admin/extension-tokens/${tokenId}`)

// ICPs
export const listICPs = () =>
  api.get<{ icps: ICP[] }>('/api/v1/admin/icps')

export const getICP = (id: string) =>
  api.get<ICP>(`/api/v1/admin/icps/${id}`)

export const createICP = (body: Partial<ICP>) =>
  api.post<ICP>('/api/v1/admin/icps', body)

export const updateICP = (id: string, body: Partial<ICP>) =>
  api.patch<ICP>(`/api/v1/admin/icps/${id}`, body)

export const deleteICP = (id: string) =>
  api.delete<{ success: boolean }>(`/api/v1/admin/icps/${id}`)

// Leads
export interface LeadFilters {
  stage?: string
  icp_id?: string
  min_score?: number
  max_score?: number
  q?: string
  limit?: number
  offset?: number
  sort?: string
  assigned_to?: string
  source?: string
}

export const listLeads = (filters: LeadFilters = {}) => {
  const params = new URLSearchParams()
  for (const [k, v] of Object.entries(filters)) {
    if (v !== undefined && v !== '') params.set(k, String(v))
  }
  const qs = params.toString() ? `?${params.toString()}` : ''
  return api.get<PaginatedResponse<Lead>>(`/api/v1/admin/leads${qs}`)
}

export const getLead = (id: string) =>
  api.get<LeadDetail>(`/api/v1/admin/leads/${id}`)

export const updateLead = (id: string, body: Partial<Lead>) =>
  api.patch<Lead>(`/api/v1/admin/leads/${id}`, body)

export const scoreLead = (id: string) =>
  api.post<{ icp_score: number; icp_confidence: string; icp_reasoning: string }>(
    `/api/v1/admin/leads/${id}/score`,
  )

export const enrolLead = (id: string, body: EnrolRequest) =>
  api.post<{ success: boolean }>(`/api/v1/admin/leads/${id}/enrol`, body)

export const bulkLeadAction = (body: BulkActionRequest) =>
  api.post<{ processed: number }>('/api/v1/admin/leads/bulk', body)

export const optOutLead = (id: string, reason: string) =>
  api.post<{ success: boolean }>(`/api/v1/admin/leads/${id}/opt-out`, { reason })

// Imports
export const uploadImport = (formData: FormData) =>
  api.upload<ImportUploadResponse>('/api/v1/admin/imports/upload', formData)

export const confirmImport = (
  batchId: string,
  body: { column_mappings: { file_column: string; lead_field: string }[]; icp_id?: string },
) => api.post<{ batch_id: string; status: string }>(`/api/v1/admin/imports/${batchId}/confirm`, body)

export const getImportBatch = (batchId: string) =>
  api.get<ImportBatch>(`/api/v1/admin/imports/${batchId}`)

export const listImportBatches = () =>
  api.get<{ batches: ImportBatch[] }>('/api/v1/admin/imports')

// Scrape jobs
export const listScrapeJobs = () =>
  api.get<{ jobs: ScrapeJob[] }>('/api/v1/admin/scrape-jobs')

export const createScrapeJob = (body: {
  source_type: string
  source_url: string
  max_followers?: number
  icp_id?: string
}) => api.post<ScrapeJob>('/api/v1/admin/scrape-jobs', body)

export const pauseScrapeJob = (id: string) =>
  api.post<{ success: boolean }>(`/api/v1/admin/scrape-jobs/${id}/pause`)

export const resumeScrapeJob = (id: string) =>
  api.post<{ success: boolean }>(`/api/v1/admin/scrape-jobs/${id}/resume`)

// Sequences
export const listSequences = () =>
  api.get<{ sequences: Sequence[] }>('/api/v1/admin/sequences')

export const getSequence = (id: string) =>
  api.get<Sequence>(`/api/v1/admin/sequences/${id}`)

export const createSequence = (body: Partial<Sequence>) =>
  api.post<Sequence>('/api/v1/admin/sequences', body)

export const updateSequence = (id: string, body: Partial<Sequence>) =>
  api.patch<Sequence>(`/api/v1/admin/sequences/${id}`, body)

export const enrolLeadsInSequence = (id: string, leadIds: string[]) =>
  api.post<{ enrolled: number }>(`/api/v1/admin/sequences/${id}/enrol`, { lead_ids: leadIds })

// Opt-outs
export const listOptOuts = () =>
  api.get<{ opt_outs: OptOut[] }>('/api/v1/admin/opt-outs')

export const addOptOut = (body: { email?: string; linkedin_url?: string; reason: string; source: string }) =>
  api.post<OptOut>('/api/v1/admin/opt-outs', body)

export const deleteOptOut = (id: string) =>
  api.delete<{ success: boolean }>(`/api/v1/admin/opt-outs/${id}`)

// Settings
export const getSettings = () =>
  api.get<Settings>('/api/v1/admin/settings')

export const updateSetting = (key: string, value: unknown) =>
  api.patch<{ setting_key: string; value: unknown }>(`/api/v1/admin/settings/${key}`, { value })

export const getRateCounters = () =>
  api.get<{ counters: RateCounter[] }>('/api/v1/admin/rate-counters')

// Dashboard
export const getDashboard = () =>
  api.get<DashboardData>('/api/v1/admin/dashboard')

// Analytics
export const getAnalytics = (range: '7d' | '30d' | '90d' = '30d') =>
  api.get<AnalyticsData>(`/api/v1/admin/analytics?range=${range}`)

// CRM metrics dashboard (Emergent port)
import type { CrmDashboardMetrics, RetirementStats } from '@/schemas/crm/crm-dashboard'

export const getCrmMetrics = () =>
  api.get<CrmDashboardMetrics>('/api/v1/admin/dashboard/crm-metrics')

export const getRetirementStats = (days: number = 90) =>
  api.get<RetirementStats>(`/api/v1/admin/dashboard/retirement-stats?days=${days}`)

// ── CRM: Tasks ───────────────────────────────────────────────────────────────

import type { Task, TaskStats, CreateTask, SnoozeTask, TaskView } from '@/schemas/crm/task'
import type { CrmUser, InviteUser, UpdateUser } from '@/schemas/crm/user'
import type { UserSettings, UpdateUserSettings } from '@/schemas/crm/user-settings'

export interface TaskFilters {
  view?: TaskView
  card_id?: string
  assignee?: string
  status?: string
  task_type?: string
  priority?: string
}

export const listTasks = (filters: TaskFilters = {}) => {
  const params = new URLSearchParams()
  for (const [k, v] of Object.entries(filters)) {
    if (v !== undefined && v !== '') params.set(k, String(v))
  }
  const qs = params.toString() ? `?${params.toString()}` : ''
  return api.get<{ tasks: Task[] }>(`/api/v1/admin/tasks${qs}`)
}

export const getTaskStats = () =>
  api.get<TaskStats>('/api/v1/admin/tasks/stats')

export const createTask = (body: CreateTask) =>
  api.post<Task>('/api/v1/admin/tasks', body)

export const updateTask = (taskId: string, body: Partial<CreateTask>) =>
  api.patch<Task>(`/api/v1/admin/tasks/${taskId}`, body)

export const completeTask = (taskId: string) =>
  api.post<{ success: boolean; task: Task }>(`/api/v1/admin/tasks/${taskId}/complete`)

export const snoozeTask = (taskId: string, body: SnoozeTask) =>
  api.post<{ success: boolean; snoozed_until: string }>(`/api/v1/admin/tasks/${taskId}/snooze`, body)

export const retireTask = (taskId: string) =>
  api.post<{ success: boolean }>(`/api/v1/admin/tasks/${taskId}/retire`)

// ── CRM: Users ───────────────────────────────────────────────────────────────

export const listCrmUsers = () =>
  api.get<{ users: CrmUser[] }>('/api/v1/admin/users')

export const getCrmUser = (userId: string) =>
  api.get<CrmUser>(`/api/v1/admin/users/${userId}`)

export const inviteUser = (body: InviteUser) =>
  api.post<CrmUser>('/api/v1/admin/users/invite', body)

export const updateCrmUser = (userId: string, body: UpdateUser) =>
  api.patch<CrmUser>(`/api/v1/admin/users/${userId}`, body)

export const deactivateUser = (userId: string) =>
  api.post<{ success: boolean }>(`/api/v1/admin/users/${userId}/deactivate`)

// ── CRM: User Settings ────────────────────────────────────────────────────────

export const getUserSettings = () =>
  api.get<UserSettings>('/api/v1/admin/settings/user/me')

export const updateUserSettings = (body: UpdateUserSettings) =>
  api.patch<UserSettings>('/api/v1/admin/settings/user/me', body)

// ── CRM: Admin PIN ────────────────────────────────────────────────────────────

export const setupAdminPin = (pin: string) =>
  api.post<{ success: boolean }>('/api/v1/admin/admin-pin/setup', { pin })

export const verifyAdminPin = (pin: string) =>
  api.post<{ success: boolean; session_token?: string }>('/api/v1/admin/admin-pin/verify', { pin })

export const changeAdminPin = (current_pin: string, new_pin: string) =>
  api.post<{ success: boolean }>('/api/v1/admin/admin-pin/change', { current_pin, new_pin })

export const logoutAdminPin = () =>
  api.post<{ success: boolean }>('/api/v1/admin/admin-pin/logout')

// ── CRM: Boards ──────────────────────────────────────────────────────────────

import type {
  Board,
  Column,
  Stream,
  Card,
  CardNote,
  AuditLogEntry,
} from '@/schemas/crm'

export const listBoards = async (params?: { archived?: boolean }) => {
  const qs = params?.archived ? '?archived=true' : ''
  const res = await api.get<{ boards: Board[] } | Board[]>(`/api/v1/admin/boards${qs}`)
  // Backend returns { boards: [...] }; unwrap. Tolerate bare array for resilience.
  return Array.isArray(res) ? res : (res.boards ?? [])
}

export const getBoard = (boardId: string) =>
  api.get<Board>(`/api/v1/admin/boards/${boardId}`)

export const createBoard = (body: { name: string; board_type: Board['board_type'] } & Partial<Board>) =>
  api.post<Board>('/api/v1/admin/boards', body)

export const updateBoard = (boardId: string, body: Partial<Board>) =>
  api.patch<Board>(`/api/v1/admin/boards/${boardId}`, body)

export const archiveBoard = (boardId: string) =>
  api.post<{ success: boolean }>(`/api/v1/admin/boards/${boardId}/archive`)

/**
 * Unwraps list responses. Backend convention: `{ items: [...] }` with the key
 * being the plural entity name. Accepts bare arrays too for resilience.
 */
async function listUnwrap<T>(path: string, key: string): Promise<T[]> {
  const res = await api.get<Record<string, unknown>>(path)
  if (Array.isArray(res)) return res as T[]
  const v = (res as Record<string, unknown>)?.[key]
  if (Array.isArray(v)) return v as T[]
  // Fallback: walk top-level keys looking for the first array value.
  for (const k of Object.keys(res ?? {})) {
    if (Array.isArray((res as Record<string, unknown>)[k])) {
      return (res as Record<string, unknown>)[k] as T[]
    }
  }
  return []
}

// ── CRM: Columns ─────────────────────────────────────────────────────────────

export const listColumns = (boardId: string) =>
  listUnwrap<Column>(`/api/v1/admin/boards/${boardId}/columns`, 'columns')

export const createColumn = (boardId: string, body: { name: string; sort_order?: number }) =>
  api.post<Column>(`/api/v1/admin/boards/${boardId}/columns`, body)

export const updateColumn = (boardId: string, columnId: string, body: Partial<Column>) =>
  api.patch<Column>(`/api/v1/admin/boards/${boardId}/columns/${columnId}`, body)

export const deleteColumn = (boardId: string, columnId: string) =>
  api.delete<{ success: boolean }>(`/api/v1/admin/boards/${boardId}/columns/${columnId}`)

export const reorderColumns = (boardId: string, orderedIds: string[]) =>
  api.post<{ success: boolean }>(`/api/v1/admin/boards/${boardId}/columns/reorder`, {
    ordered_ids: orderedIds,
  })

// ── CRM: Streams ─────────────────────────────────────────────────────────────

export const listStreams = (boardId: string) =>
  listUnwrap<Stream>(`/api/v1/admin/boards/${boardId}/streams`, 'streams')

export const createStream = (boardId: string, body: { name: string; sort_order?: number }) =>
  api.post<Stream>(`/api/v1/admin/boards/${boardId}/streams`, body)

export const updateStream = (boardId: string, streamId: string, body: Partial<Stream>) =>
  api.patch<Stream>(`/api/v1/admin/boards/${boardId}/streams/${streamId}`, body)

export const deleteStream = (boardId: string, streamId: string) =>
  api.delete<{ success: boolean }>(`/api/v1/admin/boards/${boardId}/streams/${streamId}`)

// ── CRM: Cards ───────────────────────────────────────────────────────────────

export const listCards = (boardId: string) =>
  listUnwrap<Card>(`/api/v1/admin/boards/${boardId}/cards`, 'cards')

export const listRetiredCards = (boardId: string) =>
  listUnwrap<Card>(`/api/v1/admin/boards/${boardId}/cards/retired`, 'cards')

export const getCard = (cardId: string) =>
  api.get<Card>(`/api/v1/admin/cards/${cardId}`)

export const createCard = (
  boardId: string,
  body: Partial<Card> & { title: string; column_id: string },
) => api.post<Card>(`/api/v1/admin/boards/${boardId}/cards`, body)

export const updateCard = (cardId: string, body: Partial<Card>) =>
  api.patch<Card>(`/api/v1/admin/cards/${cardId}`, body)

export const retireCard = (cardId: string, body: { retired_reason: string }) =>
  api.post<{ success: boolean }>(`/api/v1/admin/cards/${cardId}/retire`, body)

// ── CRM: Admin photo upload ───────────────────────────────────────────────────

export async function uploadAdminPhoto(file: File): Promise<{ file_id: string; url: string; mime_type: string }> {
  const fd = new FormData()
  fd.append('file', file)
  const res = await fetch(`${API_BASE}/api/v1/admin/uploads/photo`, {
    method: 'POST',
    credentials: 'include',
    body: fd,
  })
  if (!res.ok) {
    let msg = `HTTP ${res.status}`
    try { msg = (await res.json()).error ?? msg } catch { /* ignore */ }
    throw new ApiError(res.status, 'upload_failed', msg)
  }
  return res.json()
}

export const reinstateCard = (cardId: string) =>
  api.post<Card>(`/api/v1/admin/cards/${cardId}/reinstate`)

export const searchDuplicateCards = (companyName: string) =>
  listUnwrap<Card>(
    `/api/v1/admin/cards/search-duplicates?company_name=${encodeURIComponent(companyName)}`,
    'cards',
  )

// ── CRM: Card Notes ──────────────────────────────────────────────────────────

export const listCardNotes = (cardId: string) =>
  listUnwrap<CardNote>(`/api/v1/admin/cards/${cardId}/notes`, 'notes')

export const addCardNote = (cardId: string, body: { content: string }) =>
  api.post<CardNote>(`/api/v1/admin/cards/${cardId}/notes`, body)

// ── CRM: Card Audit Log ───────────────────────────────────────────────────────

export const getCardAuditLog = (cardId: string) =>
  listUnwrap<AuditLogEntry>(`/api/v1/admin/cards/${cardId}/audit`, 'audit_logs')

// ── CRM: Contacts ─────────────────────────────────────────────────────────────

export const listContacts = (search?: string) => {
  const qs = search ? `?search=${encodeURIComponent(search)}` : ''
  return listUnwrap<Card>(`/api/v1/admin/contacts${qs}`, 'cards')
}

// ── CRM: Nurturing ────────────────────────────────────────────────────────────

export const listNurturing = (search?: string) => {
  const qs = search ? `?search=${encodeURIComponent(search)}` : ''
  return listUnwrap<Card>(`/api/v1/admin/nurturing${qs}`, 'cards')
}

// ── CRM: Products (Phase 2C) ──────────────────────────────────────────────────

import type { Product, CreateProduct, UpdateProduct } from '@/schemas/crm/product'

export const listProducts = (includeInactive = false) => {
  const qs = includeInactive ? '?include_inactive=true' : ''
  return listUnwrap<Product>(`/api/v1/admin/products${qs}`, 'products')
}

export const getProduct = (productId: string) =>
  api.get<Product>(`/api/v1/admin/products/${productId}`)

export const createProduct = (body: CreateProduct) =>
  api.post<Product>('/api/v1/admin/products', body)

export const updateProduct = (productId: string, body: UpdateProduct) =>
  api.patch<Product>(`/api/v1/admin/products/${productId}`, body)

export const deleteProduct = (productId: string, permanent = false) =>
  api.delete<{ success: boolean }>(
    `/api/v1/admin/products/${productId}${permanent ? '?permanent=true' : ''}`,
  )

// ── CRM: Proposals ────────────────────────────────────────────────────────────

import type {
  Proposal,
  CreateProposal,
  UpdateProposal,
  ProposalActivity,
  ProposalFilters,
  Recipient,
} from '@/schemas/crm/proposal'

export const listProposals = (filters: ProposalFilters = {}) => {
  const params = new URLSearchParams()
  for (const [k, v] of Object.entries(filters)) {
    if (v !== undefined && v !== '' && v !== 'all') params.set(k, String(v))
  }
  const qs = params.toString() ? `?${params.toString()}` : ''
  return api.get<{ proposals: Proposal[]; total: number }>(`/api/v1/admin/proposals${qs}`)
}

export const getProposal = (proposalId: string) =>
  api.get<Proposal>(`/api/v1/admin/proposals/${proposalId}`)

export const createProposal = (body: CreateProposal) =>
  api.post<Proposal>('/api/v1/admin/proposals', body)

export const updateProposal = (proposalId: string, body: UpdateProposal) =>
  api.patch<Proposal>(`/api/v1/admin/proposals/${proposalId}`, body)

export const deleteProposal = (proposalId: string) =>
  api.delete<{ success: boolean }>(`/api/v1/admin/proposals/${proposalId}`)

export const sendProposal = (proposalId: string, body?: { recipients: Recipient[] }) =>
  api.post<{ success: boolean }>(`/api/v1/admin/proposals/${proposalId}/send`, body)

export const markProposalSigned = (proposalId: string) =>
  api.post<Proposal>(`/api/v1/admin/proposals/${proposalId}/mark-signed`)

export const markProposalDeclined = (proposalId: string) =>
  api.post<Proposal>(`/api/v1/admin/proposals/${proposalId}/mark-declined`)

export const resendToRecipient = (proposalId: string, recipientToken: string) =>
  api.post<{ success: boolean }>(`/api/v1/admin/proposals/${proposalId}/resend`, {
    recipient_token: recipientToken,
  })

export const getProposalActivities = (proposalId: string) =>
  listUnwrap<ProposalActivity>(`/api/v1/admin/proposals/${proposalId}/activities`, 'activities')

export const duplicateProposal = (proposalId: string) =>
  api.post<Proposal>(`/api/v1/admin/proposals/${proposalId}/duplicate`)

// ── CRM: Proposal Templates ───────────────────────────────────────────────────

import type {
  ProposalTemplate,
  CreateProposalTemplate,
  UpdateProposalTemplate,
} from '@/schemas/crm/proposal-template'

export const listProposalTemplates = () =>
  listUnwrap<ProposalTemplate>('/api/v1/admin/proposal-templates', 'templates')

export const getProposalTemplate = (templateId: string) =>
  api.get<ProposalTemplate>(`/api/v1/admin/proposal-templates/${templateId}`)

export const createProposalTemplate = (body: CreateProposalTemplate) =>
  api.post<ProposalTemplate>('/api/v1/admin/proposal-templates', body)

export const updateProposalTemplate = (templateId: string, body: UpdateProposalTemplate) =>
  api.patch<ProposalTemplate>(`/api/v1/admin/proposal-templates/${templateId}`, body)

// ── CRM: Library Sections ─────────────────────────────────────────────────────

import type {
  LibrarySection,
  CreateLibrarySection,
  UpdateLibrarySection,
} from '@/schemas/crm/library-section'

export const listLibrarySections = (params?: { type?: string; search?: string }) => {
  const p = new URLSearchParams()
  if (params?.type) p.set('type', params.type)
  if (params?.search) p.set('search', params.search)
  const qs = p.toString() ? `?${p.toString()}` : ''
  return listUnwrap<LibrarySection>(`/api/v1/admin/library-sections${qs}`, 'sections')
}

export const getLibrarySection = (sectionId: string) =>
  api.get<LibrarySection>(`/api/v1/admin/library-sections/${sectionId}`)

export const createLibrarySection = (body: CreateLibrarySection) =>
  api.post<LibrarySection>('/api/v1/admin/library-sections', body)

export const updateLibrarySection = (sectionId: string, body: UpdateLibrarySection) =>
  api.patch<LibrarySection>(`/api/v1/admin/library-sections/${sectionId}`, body)

export const deleteLibrarySection = (sectionId: string) =>
  api.delete<{ success: boolean }>(`/api/v1/admin/library-sections/${sectionId}`)

// ── CRM: Contracts (Phase 3B) ─────────────────────────────────────────────────

import type {
  Contract,
  ContractFilters,
  ContractTransition,
  Payment,
  PaymentFilters,
  RecordPaymentBody,
  Invoice,
  InvoiceFilters,
  CreateInvoiceBody,
  StripeSettings,
  UpdateStripeSettingsBody,
} from '@/schemas/crm/billing'

export const listContracts = (filters: ContractFilters = {}) => {
  const params = new URLSearchParams()
  for (const [k, v] of Object.entries(filters)) {
    if (v !== undefined && v !== '' && v !== 'all') params.set(k, String(v))
  }
  const qs = params.toString() ? `?${params.toString()}` : ''
  return api.get<{ contracts: Contract[]; total: number }>(`/api/v1/admin/contracts${qs}`)
}

export const getContract = (contractId: string) =>
  api.get<Contract>(`/api/v1/admin/contracts/${contractId}`)

export const updateContract = (contractId: string, body: Partial<Contract>) =>
  api.patch<Contract>(`/api/v1/admin/contracts/${contractId}`, body)

export const transitionContract = (
  contractId: string,
  action: ContractTransition,
) =>
  api.post<Contract>(`/api/v1/admin/contracts/${contractId}/${action}`)

// ── CRM: Payments (Phase 3B) ──────────────────────────────────────────────────

export const listPayments = (filters: PaymentFilters = {}) => {
  const params = new URLSearchParams()
  for (const [k, v] of Object.entries(filters)) {
    if (v !== undefined && v !== '' && v !== 'all') params.set(k, String(v))
  }
  const qs = params.toString() ? `?${params.toString()}` : ''
  return api.get<{ payments: Payment[]; total: number }>(`/api/v1/admin/payments${qs}`)
}

export const getPayment = (paymentId: string) =>
  api.get<Payment>(`/api/v1/admin/payments/${paymentId}`)

export const recordPayment = (body: RecordPaymentBody) =>
  api.post<Payment>('/api/v1/admin/payments', body)

// ── CRM: Invoices (Phase 3B) ──────────────────────────────────────────────────

export const listInvoices = (filters: InvoiceFilters = {}) => {
  const params = new URLSearchParams()
  for (const [k, v] of Object.entries(filters)) {
    if (v !== undefined && v !== '' && v !== 'all') params.set(k, String(v))
  }
  const qs = params.toString() ? `?${params.toString()}` : ''
  return api.get<{ invoices: Invoice[]; total: number }>(`/api/v1/admin/invoices${qs}`)
}

export const createInvoice = (body: CreateInvoiceBody) =>
  api.post<Invoice>('/api/v1/admin/invoices', body)

export const markInvoicePaid = (invoiceId: string) =>
  api.post<Invoice>(`/api/v1/admin/invoices/${invoiceId}/mark-paid`)

// ── CRM: Stripe Settings (Phase 3B) ───────────────────────────────────────────

export const getStripeSettings = () =>
  api.get<StripeSettings>('/api/v1/admin/stripe-settings')

export const updateStripeSettings = (body: UpdateStripeSettingsBody) =>
  api.patch<StripeSettings>('/api/v1/admin/stripe-settings', body)

// ── CRM: Intelligence (Phase 3C) ──────────────────────────────────────────────

import type {
  IntelligenceRange,
  LeaderboardResponse,
  TrendsResponse,
  LossReasonsResponse,
  ConversionFunnel,
  ProductAnalyticsResponse,
  IndustryResponse,
} from '@/schemas/crm/intelligence'

export const getLeaderboard = (range: IntelligenceRange = '30d') =>
  api.get<LeaderboardResponse>(`/api/v1/admin/intelligence/leaderboard?range=${range}`)

export const getTrends = (range: IntelligenceRange = '30d') =>
  api.get<TrendsResponse>(`/api/v1/admin/intelligence/trends?range=${range}`)

export const getLossReasons = (range: IntelligenceRange = '30d') =>
  api.get<LossReasonsResponse>(`/api/v1/admin/intelligence/loss-reasons?range=${range}`)

export const getConversionFunnel = (range: IntelligenceRange = '30d') =>
  api.get<ConversionFunnel>(`/api/v1/admin/intelligence/conversion-rate?range=${range}`)

export const getProductAnalytics = (range: IntelligenceRange = '30d') =>
  api.get<ProductAnalyticsResponse>(`/api/v1/admin/intelligence/products?range=${range}`)

export const getIndustries = (range: IntelligenceRange = '30d') =>
  api.get<IndustryResponse>(`/api/v1/admin/intelligence/industries?range=${range}`)

// ── CRM: Goals (Phase 3C) ─────────────────────────────────────────────────────

import type {
  GoalSettings,
  UpdateGoalSettings,
  GoalProgress,
  GoalItem,
  CreateGoalItem,
} from '@/schemas/crm/intelligence'

export const getGoals = () =>
  api.get<GoalSettings>('/api/v1/admin/goals')

export const updateGoals = (body: UpdateGoalSettings) =>
  api.patch<GoalSettings>('/api/v1/admin/goals', body)

export const getGoalProgress = () =>
  api.get<GoalProgress>('/api/v1/admin/goals/progress')

export const listGoalItems = async () => {
  const res = await api.get<{ items: GoalItem[] } | GoalItem[]>(
    '/api/v1/admin/goals/items',
  )
  return Array.isArray(res) ? res : (res.items ?? [])
}

export const createGoal = (body: CreateGoalItem) =>
  api.post<GoalItem>('/api/v1/admin/goals/items', body)

// ── Phase 4B: Brevo ───────────────────────────────────────────────────────────

import type {
  BrevoConnectionStatus,
  BrevoSyncLog,
  BrevoSyncResult,
  UpdateBrevoConfig,
} from '@/schemas/crm/brevo-config'

export const getBrevoConfig = () =>
  api.get<BrevoConnectionStatus>('/api/v1/admin/brevo/config')

export const updateBrevoConfig = (body: UpdateBrevoConfig) =>
  api.patch<BrevoConnectionStatus>('/api/v1/admin/brevo/config', body)

export const triggerBrevoSync = () =>
  api.post<BrevoSyncResult>('/api/v1/admin/brevo/sync')

export const listBrevoSyncLogs = (limit = 20) =>
  api.get<{ logs: BrevoSyncLog[] }>(`/api/v1/admin/brevo/sync-logs?limit=${limit}`)

// ── Phase 4B: Gmail ───────────────────────────────────────────────────────────

import type { GmailStatus, GmailSyncLog } from '@/schemas/crm/synced-email'

export const getGmailStatus = () =>
  api.get<GmailStatus>('/api/v1/admin/gmail/status')

export const updateGmailSettings = (body: { enabled: boolean }) =>
  api.patch<GmailStatus>('/api/v1/admin/gmail/status', body)

export const syncGmailContact = (contactEmail: string, hours = 48) =>
  api.post<{ messages_synced: number; contact_email: string }>(
    `/api/v1/admin/gmail/sync-contact`,
    { contact_email: contactEmail, hours },
  )

export const listGmailSyncLogs = (limit = 20) =>
  api.get<{ logs: GmailSyncLog[] }>(`/api/v1/admin/gmail/sync-logs?limit=${limit}`)

// ── Phase 4B: Synced Emails ───────────────────────────────────────────────────

import type { SyncedEmail, SyncedEmailFilters } from '@/schemas/crm/synced-email'

export const listSyncedEmails = (filters: SyncedEmailFilters = {}) => {
  const params = new URLSearchParams()
  for (const [k, v] of Object.entries(filters)) {
    if (v !== undefined && v !== '' && v !== 'all') params.set(k, String(v))
  }
  const qs = params.toString() ? `?${params.toString()}` : ''
  return api.get<{ emails: SyncedEmail[]; total: number }>(`/api/v1/admin/synced-emails${qs}`)
}

// ── Phase 4B: System Email Templates ─────────────────────────────────────────

import type {
  SystemEmailTemplate,
  CreateSystemEmail,
  UpdateSystemEmail,
  TestSendBody,
  SystemEmailFilters,
} from '@/schemas/crm/system-email'

export const listSystemEmails = (filters: SystemEmailFilters = {}) => {
  const params = new URLSearchParams()
  for (const [k, v] of Object.entries(filters)) {
    if (v !== undefined && v !== '') params.set(k, String(v))
  }
  const qs = params.toString() ? `?${params.toString()}` : ''
  return api.get<{ templates: SystemEmailTemplate[]; total: number }>(
    `/api/v1/admin/system-emails${qs}`,
  )
}

export const getSystemEmail = (templateId: string) =>
  api.get<SystemEmailTemplate>(`/api/v1/admin/system-emails/${templateId}`)

export const createSystemEmail = (body: CreateSystemEmail) =>
  api.post<SystemEmailTemplate>('/api/v1/admin/system-emails', body)

export const updateSystemEmail = (templateId: string, body: UpdateSystemEmail) =>
  api.patch<SystemEmailTemplate>(`/api/v1/admin/system-emails/${templateId}`, body)

export const deleteSystemEmail = (templateId: string) =>
  api.delete<{ success: boolean }>(`/api/v1/admin/system-emails/${templateId}`)

export const testSendSystemEmail = (templateKey: string, body: TestSendBody) =>
  api.post<{ success: boolean }>(`/api/v1/admin/system-emails/${templateKey}/test-send`, body)

// ── CRM: Bulk Send (Phase 4C) ─────────────────────────────────────────────────

import type {
  BulkSend,
  BulkSendDetail,
  BulkSendList,
  CreateBulkSend,
} from '@/schemas/crm/bulk-send'

export const listBulkSends = () =>
  api.get<BulkSendList>('/api/v1/admin/bulk-send')

export const getBulkSend = (id: string) =>
  api.get<BulkSendDetail>(`/api/v1/admin/bulk-send/${id}`)

export const createBulkSend = (body: CreateBulkSend) =>
  api.post<BulkSend>('/api/v1/admin/bulk-send', body)

export const cancelBulkSend = (id: string) =>
  api.post<{ success: boolean }>(`/api/v1/admin/bulk-send/${id}/cancel`)

/** Retry failed scheduled_send rows for a completed bulk send. */
export const retryBulkSendFailed = (id: string) =>
  api.post<{ success: boolean }>(`/api/v1/admin/bulk-send/${id}/retry-failed`)

/** Alias used by BulkSend frontend — same endpoint as listSystemEmails. */
export const listSystemEmailTemplates = () =>
  api.get<{ templates: SystemEmailTemplate[]; total: number }>('/api/v1/admin/system-emails')

// ── Phase 5B: Events calendar ─────────────────────────────────────────────────
//
// Events are cards that have a non-null event_date.
// The cards endpoint accepts has_event_date=true as a filter param.

import type { Card } from '@/schemas/crm'

export const listEventCards = (params: { board_id?: string; relationship_status?: string } = {}) => {
  const p = new URLSearchParams({ has_event_date: 'true' })
  if (params.board_id) p.set('board_id', params.board_id)
  if (params.relationship_status) p.set('relationship_status', params.relationship_status)
  return listUnwrap<Card>(`/api/v1/admin/cards?${p.toString()}`, 'cards')
}

// ── Phase 5B: Support Requests ────────────────────────────────────────────────

import type {
  SupportRequest,
  CreateSupportRequest,
  UpdateSupportRequest,
  SupportRequestFilters,
} from '@/schemas/crm/support-request'

export const listSupportRequests = (filters: SupportRequestFilters = {}) => {
  const p = new URLSearchParams()
  for (const [k, v] of Object.entries(filters)) {
    if (v !== undefined && v !== '' && v !== 'all') p.set(k, String(v))
  }
  const qs = p.toString() ? `?${p.toString()}` : ''
  return api.get<{ requests: SupportRequest[]; total: number }>(
    `/api/v1/admin/support-requests${qs}`,
  )
}

export const getSupportRequest = (requestId: string) =>
  api.get<SupportRequest>(`/api/v1/admin/support-requests/${requestId}`)

export const createSupportRequest = (body: CreateSupportRequest) =>
  api.post<SupportRequest>('/api/v1/admin/support-requests', body)

export const updateSupportRequest = (requestId: string, body: UpdateSupportRequest) =>
  api.patch<SupportRequest>(`/api/v1/admin/support-requests/${requestId}`, body)

export const assignSupportRequest = (requestId: string, userId: string) =>
  api.post<{ success: boolean }>(`/api/v1/admin/support-requests/${requestId}/assign`, {
    user_id: userId,
  })

export const addSupportNote = (requestId: string, content: string) =>
  api.post<{ note_id: string; created_at: string }>(
    `/api/v1/admin/support-requests/${requestId}/notes`,
    { content },
  )

// ── Phase 5B: Backlog ─────────────────────────────────────────────────────────

import type {
  BacklogItem,
  BacklogStats,
  CreateBacklogItem,
  UpdateBacklogItem,
  ReorderBacklog,
} from '@/schemas/crm/backlog'

export const listBacklogItems = (params: { type?: string; status?: string } = {}) => {
  const p = new URLSearchParams()
  if (params.type) p.set('type', params.type)
  if (params.status && params.status !== 'all') p.set('status', params.status)
  const qs = p.toString() ? `?${p.toString()}` : ''
  return listUnwrap<BacklogItem>(`/api/v1/admin/backlog${qs}`, 'items')
}

export const getBacklogStats = () =>
  api.get<BacklogStats>('/api/v1/admin/backlog/stats')

export const createBacklogItem = (body: CreateBacklogItem) =>
  api.post<BacklogItem>('/api/v1/admin/backlog', body)

export const updateBacklogItem = (itemId: string, body: UpdateBacklogItem) =>
  api.patch<BacklogItem>(`/api/v1/admin/backlog/${itemId}`, body)

export const deleteBacklogItem = (itemId: string) =>
  api.delete<{ success: boolean }>(`/api/v1/admin/backlog/${itemId}`)

export const reorderBacklog = (body: ReorderBacklog) =>
  api.post<{ success: boolean }>('/api/v1/admin/backlog/reorder', body)

// ── Phase 5C: Upload tokens ────────────────────────────────────────────────────

import type { UploadToken, MintUploadToken } from '@/schemas/crm/upload'

export const listUploadTokens = () =>
  api.get<{ tokens: UploadToken[] }>('/api/v1/admin/upload-tokens')

export const mintUploadToken = (body: MintUploadToken) =>
  api.post<UploadToken>('/api/v1/admin/upload-tokens', body)

export const revokeUploadToken = (tokenId: string) =>
  api.delete<{ success: boolean }>(`/api/v1/admin/upload-tokens/${tokenId}`)

// ── Phase 5C: CRM imports ──────────────────────────────────────────────────────

import type {
  CrmImportBatch,
  CrmImportUploadResponse,
  CrmConfirmImport,
} from '@/schemas/crm/crm-import'

export const uploadCrmImport = (formData: FormData) =>
  api.upload<CrmImportUploadResponse>('/api/v1/admin/crm-imports/upload', formData)

export const listCrmImports = () =>
  api.get<{ batches: CrmImportBatch[] }>('/api/v1/admin/crm-imports')

export const getCrmImportBatch = (batchId: string) =>
  api.get<CrmImportBatch>(`/api/v1/admin/crm-imports/${batchId}`)

export const confirmCrmImport = (batchId: string, body: CrmConfirmImport) =>
  api.post<{ batch_id: string; status: string }>(
    `/api/v1/admin/crm-imports/${batchId}/confirm`,
    body,
  )
